import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  removeEstimateValidationErrors,
  selectEstimateAutoFilledData,
  selectEstimateCustomer,
  selectServiceAddress,
  updateServiceAddress
} from '../../estimatesSlice';
import { CreateEstimateErrorTypes } from 'containers/estimates/constants';
import { FormCloseIcon, FormEditIcon, MapIcon, PlusIconBlue } from 'assets/icons';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import AddressModal from 'containers/jobs/components/AddressModal';
import { AddCustomerAddressPayload } from 'containers/customers/customers.model';
import { useAddCustomerAddressMutation, useEditCustomerAddressMutation } from 'containers/customers/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { getAddressAsString } from 'containers/jobs/utils';

interface ServiceAddressSectionProps {
  isEditable: boolean;
}

const ServiceAddressSection: React.FC<ServiceAddressSectionProps> = ({ isEditable }) => {
  const [openServiceAddressModal, setOpenServiceAddressModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const dispatch = useAppDispatch();
  const serviceAddress = useAppSelector(selectServiceAddress);
  const currentEstimateCustomer = useAppSelector(selectEstimateCustomer);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const autoFilledData = useAppSelector(selectEstimateAutoFilledData);

  const [addCustomerAddressMutation, { isLoading: loadingCustomerAddressAPI }] = useAddCustomerAddressMutation();
  const [editCustomerAddressMutation, { isLoading: loadingEditAddressAPI }] = useEditCustomerAddressMutation();

  const handleOpenServiceAddressModal = () => {
    setOpenServiceAddressModal(true);
  };

  const handleCloseServiceAddressModal = () => {
    setOpenServiceAddressModal(false);
    setIsEdit(false);
  };

  const handleAddressAddition = async addressDetails => {
    const isAutofillCustomerWithoutId = autoFilledData?.isAnyCustomerData && !autoFilledData?.customer?.id;

    if (isAutofillCustomerWithoutId) {
      dispatch(updateServiceAddress(addressDetails));
      dispatch(removeEstimateValidationErrors(CreateEstimateErrorTypes.SERVICE_ADDRESS));
      handleCloseServiceAddressModal();
      return;
    }

    const addressPayload: AddCustomerAddressPayload = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: currentEstimateCustomer['ID'],
      isBillingSameAsService: false,
      addressId: 0
    };

    try {
      if (isEdit) {
        const updatedAddress = await editCustomerAddressMutation({
          id: serviceAddress.id.toString(),
          body: addressPayload
        }).unwrap();
        dispatch(updateServiceAddress(updatedAddress));
      } else {
        const newAddress = await addCustomerAddressMutation(addressPayload).unwrap();
        dispatch(updateServiceAddress(newAddress));
      }
      handleCloseServiceAddressModal();
    } catch (error) {
      // Handle error (e.g., show error message to user)
    }
  };

  const handleRemoveServiceAddress = () => {
    dispatch(updateServiceAddress(null));
  };

  const handleEditServiceAddress = () => {
    setIsEdit(true);
    setOpenServiceAddressModal(true);
  };

  return (
    <div className="relative">
      <div className="pb-2.5 text-17px font-semibold text-heading">Service address</div>
      <div className="relative rounded border border-borderGray p-5">
        {serviceAddress ? (
          <div className="flex items-start gap-1">
            <MapIcon />
            <div>
              <div className="text-sbase font-semibold text-primaryText">
                {getAddressAsString(serviceAddress) ?? '-'}
              </div>
              {serviceAddress.addressNote && (
                <div className="mt-1 text-sbase font-semibold text-primaryText">{serviceAddress.addressNote}</div>
              )}
            </div>
          </div>
        ) : (
          <div
            className="flex cursor-pointer items-center gap-1"
            onClick={handleOpenServiceAddressModal}
            role="presentation">
            <PlusIconBlue />
            <div className="text-17px font-semibold text-secondary">Add a service address</div>
          </div>
        )}
        {serviceAddress && isEditable && (
          <div className="absolute top-1/2 -right-6 -translate-y-1/2 space-y-1.5">
            <FormCloseIcon className="cursor-pointer" onClick={handleRemoveServiceAddress} />
            <FormEditIcon className="cursor-pointer" onClick={handleEditServiceAddress} />
          </div>
        )}
      </div>
      {openServiceAddressModal && (
        <AddressModal
          onClose={handleCloseServiceAddressModal}
          type={ADDRESS_TYPE.SERVICE}
          onAddAddress={handleAddressAddition}
          isEdit={isEdit}
          initialAddress={serviceAddress}
          isLoading={loadingCustomerAddressAPI || loadingEditAddressAPI}
        />
      )}
    </div>
  );
};

export default ServiceAddressSection;
